import axios, { AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { clearTokens, getStoredTokens } from './auth/tokenStorage';
import { refreshAccessToken } from './auth/tokenManager';

// The API speaks snake_case; types below mirror the wire format directly.

export const api = axios.create({ baseURL: '/api', timeout: 30000 });

api.interceptors.request.use((config) => {
  const tokens = getStoredTokens();
  if (tokens) config.headers.Authorization = `${tokens.tokenType || 'Bearer'} ${tokens.accessToken}`;
  return config;
});

// One retry after a silent refresh; if that fails the session is over.
api.interceptors.response.use(
  (r) => r,
  async (error: AxiosError) => {
    const original = error.config as (InternalAxiosRequestConfig & { _retried?: boolean }) | undefined;
    if (error.response?.status === 401 && original && !original._retried) {
      original._retried = true;
      const fresh = await refreshAccessToken();
      if (fresh) return api(original);
      clearTokens();
    }
    return Promise.reject(error);
  }
);

/** Human-readable message from an API error ({ error, message } body) or a network failure. */
export function errorMessage(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as { message?: string; error?: string } | undefined;
    if (data?.message) return data.message;
    if (data?.error) return data.error;
    if (err.response) return `Request failed (${err.response.status})`;
    return 'Could not reach the server';
  }
  return err instanceof Error ? err.message : 'Something went wrong';
}

// 1 = supervisor, 2 = operator
export type AccessLevel = 1 | 2;

export const ACCESS_LEVELS: Record<AccessLevel, string> = { 1: 'Supervisor', 2: 'Operator' };

export interface User {
  id: number;
  name: string;
  access_level: AccessLevel;
  created_at: string;
  updated_at: string;
}

export interface UserInput {
  name?: string;
  access_level?: AccessLevel;
}

export const usersApi = {
  list: () => api.get<User[]>('/users').then((r) => r.data),
  create: (input: UserInput) => api.post<User>('/users', input).then((r) => r.data),
  update: (id: number, input: UserInput) => api.put<User>(`/users/${id}`, input).then((r) => r.data),
  remove: (id: number) => api.delete(`/users/${id}`),
};

// One result sent from a workstation by semmco-eebd.exe sendData.
export interface Test {
  id: number;
  serial: string;
  stage: string | null;
  user_id: number;
  user_name: string;
  station: number;
  tested_at: string;
  pressure1: number | null;
  pressure2: number | null;
  pressure3: number | null;
  voltage: number | null;
  atmospheric_pressure: number | null;
  received_at: string;
}

// Same fields the station sends; user is the name, barcode includes the stage letter.
export interface TestInput {
  user: string;
  barcode: string;
  station: number;
  tested_at: string;
  pressure1: number | null;
  pressure2: number | null;
  pressure3: number | null;
  voltage: number | null;
  atmospheric_pressure: number | null;
}

export const testsApi = {
  // Newest 1000, optionally filtered by serial.
  list: (serial?: string) =>
    api.get<Test[]>('/tests', { params: serial ? { serial } : undefined }).then((r) => r.data),
  update: (id: number, input: TestInput) => api.put<Test>(`/tests/${id}`, input).then((r) => r.data),
  remove: (id: number) => api.delete(`/tests/${id}`),
};

// The API sends UTC timestamps, sometimes without the Z.
export function parseUtc(value: string): Date {
  return new Date(value.endsWith('Z') || value.includes('+') ? value : `${value}Z`);
}

export function formatDateTime(value: string | null | undefined): string {
  if (!value) return '—';
  const d = parseUtc(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleString();
}

// The exe + readme as a zip. Fetched with the token, then handed to the browser to save.
export async function downloadStationUtility(): Promise<void> {
  const res = await api.get<Blob>('/downloads/semmco-eebd.zip', { responseType: 'blob', timeout: 120000 });
  const url = URL.createObjectURL(res.data);
  const link = document.createElement('a');
  link.href = url;
  // The server names the file with the version, e.g. semmco-eebd_v1.0.0.zip.
  const header = String(res.headers['content-disposition'] ?? '');
  link.download = /filename="?([^";]+)/.exec(header)?.[1] ?? 'semmco-eebd.zip';
  link.click();
  URL.revokeObjectURL(url);
}
